import React from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  Clock,
  ShieldCheck,
  Award,
  Wrench,
  Phone,
  Check,
  ArrowRight,
} from "lucide-react";
import { services, serviceFaqs } from "../mock/mock";
import Seo from "../seo/Seo";
import { serviceSchema, breadcrumbSchema, webPageSchema, faqSchema } from "../seo/schema";
import CircleArrowLink from "../components/common/CircleArrowLink";
import Reveal from "../components/common/Reveal";
import FAQ from "../components/sections/FAQ";
import WhyChooseUs from "../components/sections/WhyChooseUs";

const iconMap = {
  clock: Clock,
  shield: ShieldCheck,
  award: Award,
  wrench: Wrench,
  phone: Phone,
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const seoTitle = `${service.title} in Freehold, NJ | Talk N' Fix`;
  const seoDesc = `${service.short} Same-day ${service.title.toLowerCase()} with a 6-month warranty at Talk N' Fix inside Freehold Raceway Mall, NJ.`;
  const path = `/services/${service.slug}`;
  const faqItems = serviceFaqs[service.slug] || [];

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={path}
        image={service.image}
        schema={[
          serviceSchema(service),
          webPageSchema({ title: seoTitle, description: seoDesc, path }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path },
          ]),
          ...(faqItems.length ? [faqSchema(faqItems)] : []),
        ]}
      />
      {/* Hero */}
      <section className="relative min-h-[58vh] flex items-center overflow-hidden">
        <img
          src={service.heroBg}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
        <div className="tnf-container relative pt-32 pb-16">
          <Reveal className="max-w-2xl">
            <span className="tnf-eyebrow text-[var(--tnf-orange)]">
              Talk N' Fix · Freehold
            </span>
            <h1 className="font-display text-white font-extrabold leading-[1.05] text-[42px] sm:text-[58px] mt-3">
              {service.heroTitle}
            </h1>
            <p className="text-white/80 text-[17px] mt-5 max-w-xl leading-relaxed">
              {service.short}
            </p>
            <div className="mt-8">
              <CircleArrowLink to="/repair-estimate" variant="white">
                Book repair slot
              </CircleArrowLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Intro + image */}
      <section className="py-20 lg:py-24">
        <div className="tnf-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal className="rounded-[24px] overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.12)]">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-full object-cover aspect-[4/3]"
              />
            </Reveal>
            <Reveal delay={120}>
              <p className="text-[#3b3b3b] text-[17px] leading-[1.7]">
                {service.intro}
              </p>
            </Reveal>
          </div>

          {/* Badges */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            {service.badges.map((b, i) => {
              const Icon = iconMap[b.icon] || ShieldCheck;
              return (
                <Reveal
                  key={i}
                  delay={i * 90}
                  className="bg-white rounded-2xl border border-black/5 p-6 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)] transition-shadow"
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: "rgba(5,165,213,0.12)" }}
                  >
                    <Icon size={22} color="var(--tnf-orange)" />
                  </div>
                  <h3 className="font-display text-[18px] font-bold text-[#0e0e10]">
                    {b.title}
                  </h3>
                  <p className="text-[14px] text-[#3b3b3b]/80 mt-2 leading-relaxed">
                    {b.text}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common problems */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="tnf-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="tnf-eyebrow">What we fix</span>
            <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              {service.problemsTitle}
            </h2>
            <p className="text-[#3b3b3b]/80 text-[16px] mt-3">
              {service.problemsIntro}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {service.problems.map((p, i) => (
              <Reveal
                key={i}
                delay={(i % 3) * 80}
                className="group flex gap-4 p-5 rounded-2xl border border-black/5 bg-[#f6f7f9] hover:bg-[#0e0e10] transition-colors duration-300"
              >
                <span
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "var(--tnf-orange)" }}
                >
                  <Check size={16} color="#fff" strokeWidth={3} />
                </span>
                <div>
                  <h4 className="font-display text-[16px] font-bold text-[#0e0e10] group-hover:text-white transition-colors">
                    {p.title}
                  </h4>
                  <p className="text-[13.5px] text-[#3b3b3b]/80 group-hover:text-white/70 mt-1 leading-relaxed transition-colors">
                    {p.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3-step process */}
      <section className="py-20 lg:py-24">
        <div className="tnf-container">
          <Reveal className="max-w-2xl mb-12">
            <span className="tnf-eyebrow">How it works</span>
            <h2 className="font-display text-[30px] sm:text-[40px] font-extrabold text-[#0e0e10] mt-3 leading-[1.1]">
              Your 3-Step Repair Process
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {service.steps.map((step, i) => (
              <Reveal
                key={i}
                delay={i * 110}
                className="relative bg-white rounded-2xl border border-black/5 p-7 overflow-hidden"
              >
                <span className="absolute -top-4 -right-2 font-display text-[88px] font-extrabold text-black/[0.04] leading-none">
                  {i + 1}
                </span>
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white font-display font-bold mb-5"
                  style={{ background: "var(--tnf-blue)" }}
                >
                  {i + 1}
                </div>
                <div className="text-[12px] font-semibold uppercase tracking-wider text-[var(--tnf-orange)] mb-1">
                  {step.note}
                </div>
                <h3 className="font-display text-[19px] font-bold text-[#0e0e10]">
                  {step.title}
                </h3>
                <p className="text-[14px] text-[#3b3b3b]/80 mt-2 leading-relaxed">
                  {step.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-20 lg:pb-28">
        <div className="tnf-container">
          <Reveal className="relative rounded-[28px] overflow-hidden bg-[#0e0e10] px-8 sm:px-14 py-14 text-center">
            <div
              className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-30"
              style={{ background: "var(--tnf-orange)" }}
            />
            <div
              className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-20"
              style={{ background: "var(--tnf-blue)" }}
            />
            <div className="relative">
              <h2 className="font-display text-white text-[30px] sm:text-[42px] font-extrabold leading-[1.1]">
                Ready to Get Your Life Back?
              </h2>
              <p className="text-white/70 text-[16px] mt-4 max-w-xl mx-auto">
                Experience the difference of Speed, Quality, and 6-Month Guaranteed
                service at Talk N' Fix in Freehold, New Jersey.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <CircleArrowLink to="/repair-estimate" variant="orange">
                  Book your repair today
                </CircleArrowLink>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/20 text-white text-[15px] font-semibold hover:bg-white/10 transition-colors"
                >
                  Contact us <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <FAQ
        items={faqItems}
        eyebrow="FAQs"
        title={`${service.title} — Common Questions`}
        subtitle="Quick answers about pricing, turnaround and warranty for your repair in Freehold."
        className="bg-white"
      />

      <WhyChooseUs />
    </>
  );
};

export default ServiceDetail;
